import axios from '../../config/axiosConfig';
import { all, call, cancelled, put, takeLatest } from 'redux-saga/effects';
import { loadAllSGInfoFailure, loadAllSGInfoSuccess, loadItemCountFailure, loadItemCountSuccess, loadSGInfoFailure, loadSGInfoSuccess } from './queryPanel.actions';

import {
  ItemCountRequest,
  ItemCountResponse,
  QueryPanelActionTypes,
  SubjectGradeInfoRequest,
  SubjectGradeInfoResponse
} from './models';

/**
 * Fetches all subject grade info for querypanel from db,
 * then dispatches an action with the corresponding result
 */
export function* loadAllSGInfoAsyncSaga(): Generator {
  try {
    const response = yield axios.get<SubjectGradeInfoResponse>('api/querypanel/allsubjectgradeinfo')
      .then((res) => res.data);
    yield put(loadAllSGInfoSuccess(<SubjectGradeInfoResponse>response));
  } catch (error) {
    yield put(loadAllSGInfoFailure(error));
  }
}

/**
 * Fetches info for a subject and grade querypanel from db,
 * then dispatches an action with the corresponding result
 */
export function* loadSGInfoAsyncSaga({payload}: {payload: SubjectGradeInfoRequest; type: string}): Generator {
  const controller = new AbortController();
  const signal = controller.signal;
  try {
    const response = yield axios.post<SubjectGradeInfoResponse>('api/querypanel/subjectgradeinfo', {
      ...payload
    }, {signal})
      .then((res) => res.data);
    yield put(loadSGInfoSuccess(<SubjectGradeInfoResponse>response));
  } catch (error) {
    yield put(loadSGInfoFailure(error));
  } finally {
    // if cancelled, abort the request
    if (yield cancelled()) {
      controller.abort();
    }
  }
}

/**
 * Fetches info for a subject and grade querypanel from db,
 * then dispatches an action with the corresponding result
 */
export function* loadItemCountAsyncSaga({payload}: {payload: ItemCountRequest; type: string}): Generator {
  const controller = new AbortController();
  const signal = controller.signal;
  try {
    const response = yield axios.post<ItemCountResponse>('api/querypanel/itemcount', {
      ...payload
    }, {signal})
      .then((res) => ({itemCount: res.data}));
    yield put(loadItemCountSuccess(<ItemCountResponse>response));
  } catch (error) {
    yield put(loadItemCountFailure(error));
  } finally {
    // if cancelled, abort the request
    if (yield cancelled()) {
      controller.abort();
    }
  }
}

/**
 * Listens for LOAD_ALL_SG_INFO_START action, dispatchs loadAllSGInfoAsyncSaga
 */
export function* loadAllSGInfoStartSaga(): Generator {
  yield takeLatest(QueryPanelActionTypes.LOAD_ALL_SG_INFO_START, loadAllSGInfoAsyncSaga);
}

/**
 * Listens for LOAD_SG_INFO_START action, dispatchs loadSGInfoAsyncSaga
 */
export function* loadSGInfoStartSaga(): Generator {
  yield takeLatest(QueryPanelActionTypes.LOAD_SG_INFO_START, loadSGInfoAsyncSaga);
}

/**
 * Listens for LOAD_ITEM_COUNT_START action, dispatchs loadItemCountAsyncSaga
 */
export function* loadItemCountStartSaga(): Generator {
  yield takeLatest(QueryPanelActionTypes.LOAD_ITEM_COUNT_START, loadItemCountAsyncSaga);
}

/**
 * Composite of all queryPanel sagas that are listening for external actions
 */
export function* queryPanelSagas(): Generator {
  yield all([call(loadAllSGInfoStartSaga), call(loadSGInfoStartSaga), call(loadItemCountStartSaga)]);
}

export default queryPanelSagas;